import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/hooks/useFocusTrap';

/** Right-side slide-over. Evidence + copilot surfaces sit highest (brief §6.2). */
export function Drawer({
  open,
  onClose,
  title,
  children,
  widthClass = 'w-full max-w-[440px]',
  accent = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  widthClass?: string;
  /** copper-keylined when the surface is AI/evidence content. */
  accent?: boolean;
}) {
  const titleId = useId();
  const ref = useFocusTrap<HTMLDivElement>(open, onClose);

  if (!open) return null;

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="fixed inset-0 z-50 outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px] animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          'absolute right-0 top-0 flex h-full flex-col bg-surface shadow-lg animate-fade-in',
          'border-l',
          accent ? 'border-l-ai/60' : 'border-l-border-default',
          widthClass,
        )}
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <div id={titleId} className="text-sm font-semibold text-text-primary">{title}</div>
          <button
            onClick={onClose}
            className="rounded-sm p-1 text-text-tertiary hover:bg-hover hover:text-text-primary"
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </aside>
    </div>
  );
}
